import React, { useCallback, useMemo } from "react";
import { searchCarriers } from "./api";
import { RecordTypeahead } from "./RecordTypeahead";

/**
 * Carrier suggestion field for the gate check-in's Carrier Name.
 *
 * Same shape as {@link FacilityTypeahead} and for the same reason: picking a known carrier fills
 * the whole Carrier block beside it — office code, address, city, state, zip — while a carrier we
 * have no record of can still simply be typed. A gate that can only check in carriers we already
 * know about is a gate that turns freight away.
 *
 * Unlike the facility search this takes no office code: a carrier is whoever the freight moves
 * with, and there is no rule that they trade at the yard's own office.
 */
export function CarrierTypeahead({
  label,
  value,
  onChange,
  onPick,
  className = "",
  disabled = false,
  minChars = 2,
  placeholder,
}) {
  const search = useCallback((query) => searchCarriers(query), []);

  const getLabel = useCallback((carrier) => String(carrier?.name ?? "").trim(), []);

  // Office code leads the secondary line: it is the field the guard is most often reconciling
  // against the paperwork, and it is what disambiguates two similarly named carriers.
  const getSecondary = useMemo(
    () => (carrier) =>
      [carrier?.officeCode, carrier?.addressLine1, carrier?.city, carrier?.state, carrier?.zipCode]
        .map((p) => String(p ?? "").trim())
        .filter(Boolean)
        .join(" · "),
    []
  );

  const getKey = useCallback((carrier, i) => String(carrier?.officeCode ?? i), []);

  return (
    <RecordTypeahead
      label={label}
      value={value}
      onChange={onChange}
      onPick={onPick}
      search={search}
      getLabel={getLabel}
      getSecondary={getSecondary}
      getKey={getKey}
      className={className}
      disabled={disabled}
      minChars={minChars}
      placeholder={placeholder}
    />
  );
}

export default CarrierTypeahead;
